import { Link } from 'react-router-dom'
import { ArrowRight, ArrowLeft, Sparkles, Play, FilmIcon, Scissors, Clock, Download, BookOpenCheck, Plus, Save, ArrowRightFromLineIcon } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/TimelinePage.stylex'
import { useState } from 'react'

interface TimelineStepProps {
    title: string
    description?: string
    descriptionParts?: (string | React.ReactNode)[]
    icon: React.ReactNode
    imageUrl: string[]
    features: string[]
    className?: any
    stepNumber: number
    isLast: boolean
}

function TimelineStep({
    title,
    description,
    descriptionParts,
    icon,
    imageUrl,
    features,
    stepNumber,

    isLast
}: TimelineStepProps) {
    return (
        <div {...stylex.props(styles.stepRow)}>
            <div {...stylex.props(styles.stepNumber, isLast && styles.noNumberLine)}>
                {stepNumber}
            </div>

            <div {...stylex.props(styles.stepContent)}>
                <div {...stylex.props(styles.stepHeader)}>
                    <div {...stylex.props(styles.stepIcon)}>{icon}</div>
                    <h3 {...stylex.props(styles.stepTitle)}>{title}</h3>
                </div>

                <p {...stylex.props(styles.stepDescription)}>
                    {descriptionParts ? descriptionParts : description}
                </p>

                <ul {...stylex.props(styles.stepFeatures)}>
                    {features.map((feature, index) => (
                        <li key={index} {...stylex.props(styles.stepFeature)}>
                            {index + 1}. {feature}
                        </li>
                    ))}
                </ul>
            </div>

            <div {...stylex.props(
                styles.stepImageGallery,
                imageUrl.length === 1 ? styles.gallerySingle : styles.galleryMulti
            )}>
                {imageUrl.map((image, index) => (
                    <div key={index} {...stylex.props(styles.stepImageContainer)}>
                        <img
                            src={image}
                            {...stylex.props(styles.stepImage)}
                        />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function TimelinePage() {
    const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

    const timelineSteps = [
        {
            title: "Add Media to Timeline",
            description: "Start by adding media to your timeline. You can add images, videos, audio files, and more from your workspace or projects.",
            icon: <Plus size={24} />,
            imageUrl: ["https://cdn.aibrush.co/docs/qstart/sample-8.png"],
            features: ["Click on the plus button to add media to the timeline"],
        },
        {
            title: "Arrange on Timeline",
            description: "Organize your media on the timeline by dragging and dropping clips into the desired order. Create sequences, layer multiple elements, and build your story.",
            icon: <Clock size={24} />,
            imageUrl: ["https://cdn.aibrush.co/docs/qstart/sample-9.png"],
            features: ["Drag and drop arrangement", "Create sequences", "Layer multiple elements"],
        },
        {
            title: "Save Your Edit",
            description: "Remember to save your edits",
            icon: <Save size={24} />,
            imageUrl: ["https://cdn.aibrush.co/docs/qstart/sample-10.png"],
            features: ["Save your edits", "Keep your progress safe"],
        },
        {
            title: "Edit and Trim",
            description: "Use the timeline editor to trim, cut, and adjust your media clips. Fine-tune timing, remove unwanted sections, and perfect your content.",
            icon: <Scissors size={24} />,
            imageUrl: ["https://cdn.aibrush.co/docs/qstart/sample-11.png"],
            features: ["Click on the trim button to edit the media", "Cut and split clips", "Click 'ESC' button to exit trim mode"],
        },
        {
            title: "Export Your Project",
            description: "When you're satisfied with your timeline, export your project in various formats. Share your creations or import them into other applications.",
            icon: <ArrowRightFromLineIcon size={24} />,
            imageUrl: ["https://cdn.aibrush.co/docs/qstart/sample-12.png"],
            features: ["Save you changes", "Click on the export button to export your project", "It will take a few seconds to export your project"],
        },
        {
            title: "View The Results",
            description: "When you're satisfied with your timeline, view the results of your project.",
            icon: <Download size={24} />,
            imageUrl: ["https://cdn.aibrush.co/docs/qstart/sample-14.png", "https://cdn.aibrush.co/docs/qstart/sample-13.png"],
            features: ["Click on the 'View Results' button to view the final video", "You can also download the video to your computer"],
        },
    ]

    const additionalFeatures = [
        { title: "Explore Examples", description: "Explore Full Projects Examples created with AiBrush Studio.", icon: <BookOpenCheck size={20} />, href: "/examples" },
        { title: "Quick Start Guide", description: "Get up and running with AiBrush Studio in minutes.", icon: <Play size={20} />, href: "/quick-start" },
        { title: "Workspace", description: "Explore the workspace and learn how to use it to create any media type.", icon: <FilmIcon size={20} />, href: "/workspace" },
        { title: "AI Models", description: "Discover all available AI models and their capabilities.", icon: <Sparkles size={20} />, href: "/ai-models" }
    ]

    return (
        <div {...stylex.props(styles.timelinePage)}>
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            <div {...stylex.props(styles.pageHeader)}>
                <div {...stylex.props(styles.headerContent)}>
                    <h1 {...stylex.props(styles.pageTitle)}>
                        <FilmIcon size={32} {...stylex.props(styles.titleIcon)} />
                        Timeline Editor Guide
                    </h1>
                    <p {...stylex.props(styles.pageSubtitle)}>
                        Master the timeline editor to create professional videos, presentations, and multimedia content. Learn how to arrange, edit, and export your AI-generated media.
                    </p>
                </div>
            </div>

            <section {...stylex.props(styles.timelineSection)}>
                <div {...stylex.props(styles.stepsContainer)}>
                    {timelineSteps.map((step, index) => (
                        <TimelineStep
                            key={index}
                            {...step}
                            stepNumber={index + 1}
                            isLast={index === timelineSteps.length - 1}
                            className={null}
                        />
                    ))}
                </div>
            </section>

            <section {...stylex.props(styles.additionalFeaturesSection)}>
                <div {...stylex.props(styles.featuresGrid)}>
                    {additionalFeatures.map((feature, index) => (
                        <Link
                            key={index}
                            to={feature.href}
                            {...stylex.props(styles.featureCard)}
                            onMouseEnter={() => setHoveredFeature(index)}
                            onMouseLeave={() => setHoveredFeature(null)}
                        >
                            <div {...stylex.props(styles.featureIcon)}>{feature.icon}</div>
                            <div {...stylex.props(styles.featureContent)}>
                                <h3 {...stylex.props(styles.featureTitle)}>{feature.title}</h3>
                                <p {...stylex.props(styles.featureDescription)}>{feature.description}</p>
                            </div>
                            <ArrowRight
                                size={16}
                                {...stylex.props(
                                    styles.featureArrow,
                                    hoveredFeature === index && styles.featureArrowActive
                                )}
                            />
                        </Link>
                    ))}
                </div>
            </section>

            <section {...stylex.props(styles.ctaSection)}>
                <div {...stylex.props(styles.ctaContent)}>
                    <h2 {...stylex.props(styles.ctaTitle)}>Ready to Edit Your Timeline?</h2>
                    <p {...stylex.props(styles.ctaDescription)}>
                        Jump into your projects and start editing amazing movies with your AI-generated media.
                    </p>
                    <div {...stylex.props(styles.ctaButtons)}>
                        <Link to="https://aibrush.co/projects" {...stylex.props(styles.primaryButton)}>
                            <Play size={16} />
                            Go to Your Projects
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}